import axios from "axios";
import mapboxgl from "mapbox-gl";
import React, { useEffect, useState } from "react";
import { MAP_TOKEN } from "../../../helpers/config";

mapboxgl.accessToken = MAP_TOKEN as string;

interface Props {
  currentLocation: { lat?: number; lon?: number };
  destination: { lat?: number; lon?: number };
}

const DistanceCalculator: React.FC<Props> = ({
  currentLocation,
  destination,
}) => {
  const [distance, setDistance] = useState<number | null>(null);
  const [duration, setDuration] = useState<number | null>(null);

  useEffect(() => {
    const map = new mapboxgl.Map({
      container: "map",
      style: "mapbox://styles/mapbox/streets-v11",
      center: [90.3621026, 23.7055748],
      zoom: 12,
    });

    const fetchRoute = async () => {
      try {
        const response = await axios.get(
          `https://api.mapbox.com/directions/v5/mapbox/driving/${currentLocation.lon},${currentLocation.lat};${destination.lon},${destination.lat}`,
          {
            params: {
              access_token: mapboxgl.accessToken,
              geometries: "geojson",
            },
          }
        );

        const data = response?.data;
        const route = data?.routes[0];

        if (route) {
          setDistance(parseFloat((route.distance / 1000).toFixed(2))); // Convert to KM
          setDuration(Math.ceil(route.duration / 60)); // Convert to Minutes

          map.on("load", () => {
            map.setCenter([
              ((currentLocation?.lon || 0) + (destination?.lon || 0)) / 2,
              ((currentLocation?.lat || 0) + (destination?.lat || 0)) / 2,
            ]);

            map.addSource("route", {
              type: "geojson",
              data: {
                type: "Feature",
                geometry: route.geometry,
              } as any,
            });

            map.addLayer({
              id: "route",
              type: "line",
              source: "route",
              layout: {
                "line-join": "round",
                "line-cap": "round",
              },
              paint: {
                "line-color": "#33AB29",
                "line-width": 4,
              },
            });

            // Markers for Current Location and Destination
            const addCircleMarker = (
              lat: number,
              lon: number,
              color: string
            ) => {
              map.addSource(`circle-${lat}-${lon}`, {
                type: "geojson",
                data: {
                  type: "Feature",
                  geometry: { type: "Point", coordinates: [lon, lat] },
                  properties: {},
                },
              });

              map.addLayer({
                id: `circle-layer-${lat}-${lon}`,
                type: "circle",
                source: `circle-${lat}-${lon}`,
                paint: {
                  "circle-radius": 6,
                  "circle-color": color,
                  "circle-opacity": 1,
                },
              });
            };

            addCircleMarker(
              currentLocation.lat || 0,
              currentLocation.lon || 0,
              "#33AB29"
            );
            addCircleMarker(
              destination.lat || 0,
              destination.lon || 0,
              "#FF3838"
            );
          });
        }
      } catch (err) {
        console.error("Error fetching route:", err);
      }
    };

    fetchRoute();

    return () => map.remove();
  }, [currentLocation, destination]);

  return (
    <div className="mt-[66px]">
      <div id="map" style={{ width: "100%", height: "400px" }}></div>
      {distance !== null && duration !== null && (
        <div className="mt-4 p-4 bg-gray-100 rounded-md shadow">
          <p className="text-lg font-semibold">Route Details:</p>
          <p>
            📏 Distance: <strong>{distance} km</strong>
          </p>
          <p>
            ⏳ Estimated Time: <strong>{duration} min</strong>
          </p>
        </div>
      )}
    </div>
  );
};

export default DistanceCalculator;
